import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Add,
  Home as HomeIcon,
  Delete as DeleteIcon,
  AutoAwesome as EmbeddingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setTables,
  setSelectedTable,
  setPage,
  setPageSize,
  setSearch,
  setSchemaIdFilter,
  clearSelectedTable,
  addTable,
  updateTable as updateTableAction,
  deleteTable as deleteTableAction,
} from '../../store/slices/tablesSlice';
import { GET_TABLES_LIST, GET_TABLE } from '../../graphql/queries/tables.queries';
import { GET_SCHEMAS_LIST } from '../../graphql/queries/schemas.queries';
import {
  CREATE_TABLE,
  UPDATE_TABLE,
  DELETE_TABLE,
  CREATE_TABLE_EMBEDDING,
} from '../../graphql/mutations/tables.mutations';
import {
  FindTableListInput,
  FindTableInput,
  CreateTableInput,
  UpdateTableInput,
  DeleteTableInput,
  CreateTableEmbeddingInput,
  CreateTableResponse,
  UpdateTableResponse,
  DeleteTableResponse,
  CreateTableEmbeddingResponse,
  TablesListResponse,
  TableResponse,
  Table,
} from '../../types/tables.types';
import {
  SchemasListResponse,
  FindSchemaListInput,
} from '../../types/schemas.types';
import ViewTableModal from '../../components/Tables/ViewTableModal';
import CreateEditTableModal, {
  TableFormData,
} from '../../components/Tables/CreateEditTableModal';
import ConfirmDeleteTableDialog from '../../components/Tables/ConfirmDeleteTableDialog';
import ConfirmCreateEmbeddingDialog from '../../components/Tables/ConfirmCreateEmbeddingDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './Tables.css';

const Tables: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tables, selectedTable, loading, error, pagination, search, schemaIdFilter } =
    useAppSelector((state) => state.tables);

  const [searchInput, setSearchInput] = useState(search);
  const [schemaFilter, setSchemaFilter] = useState(schemaIdFilter);
  const [schemas, setSchemas] = useState<any[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmEmbeddingOpen, setConfirmEmbeddingOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<Table | null>(null);
  const [tableForEmbedding, setTableForEmbedding] = useState<Table | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingEmbedding, setGeneratingEmbedding] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchTables, { loading: fetchingList }] = useLazyQuery<
    { tables: TablesListResponse },
    { params: FindTableListInput }
  >(GET_TABLES_LIST, {
    fetchPolicy: 'network-only',
  });

  const [fetchTable] = useLazyQuery<
    { table: TableResponse },
    { params: FindTableInput }
  >(GET_TABLE, {
    fetchPolicy: 'network-only',
  });

  const [fetchSchemas] = useLazyQuery<
    { schemas: SchemasListResponse },
    { params: FindSchemaListInput }
  >(GET_SCHEMAS_LIST, {
    fetchPolicy: 'network-only',
  });

  const [createTableMutation] = useMutation<
    { createTable: CreateTableResponse },
    { params: CreateTableInput }
  >(CREATE_TABLE);

  const [updateTableMutation] = useMutation<
    { updateTable: UpdateTableResponse },
    { params: UpdateTableInput }
  >(UPDATE_TABLE);

  const [deleteTableMutation] = useMutation<
    { deleteTable: DeleteTableResponse },
    { params: DeleteTableInput }
  >(DELETE_TABLE);

  const [createTableEmbeddingMutation] = useMutation<
    { createTableEmbedding: CreateTableEmbeddingResponse },
    { params: CreateTableEmbeddingInput }
  >(CREATE_TABLE_EMBEDDING);

  useEffect(() => {
    loadSchemas();
  }, []);

  useEffect(() => {
    loadTables();
  }, [pagination.page, pagination.pageSize, search, schemaIdFilter]);

  const loadSchemas = async () => {
    try {
      const { data } = await fetchSchemas({
        variables: {
          params: {
            search: null,
            page: 1,
            pageSize: 100,
          },
        },
      });

      if (data?.schemas?.success && data.schemas.data) {
        setSchemas(data.schemas.data.schemas);
      }
    } catch (err: any) {
      console.error('Error loading schemas:', err);
    }
  };

  const loadTables = async () => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchTables({
        variables: {
          params: {
            schemaId: schemaIdFilter || null,
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.tables?.success && data.tables.data) {
        dispatch(setTables(data.tables.data));
      } else {
        const errorMessage = data?.tables?.message || 'Error al cargar tablas';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading tables:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSchemaFilterChange = (value: string) => {
    setSchemaFilter(value);
    dispatch(setSchemaIdFilter(value));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchTable({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.table?.success && data.table.data && data.table.data.length > 0) {
        const table = data.table.data[0];
        dispatch(setSelectedTable(table));
        setViewModalOpen(true);
      } else {
        const errorMessage = data?.table?.message || 'Error al cargar tabla';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading table:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchTable({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.table?.success && data.table.data && data.table.data.length > 0) {
        const table = data.table.data[0];
        dispatch(setSelectedTable(table));
        setModalMode('edit');
        setCreateEditModalOpen(true);
      } else {
        const errorMessage = data?.table?.message || 'Error al cargar tabla';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading table:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedTable());
    setModalMode('create');
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (data: TableFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        const { data: responseData } = await createTableMutation({
          variables: {
            params: {
              schemaId: data.schemaId,
              schemaName: data.schemaName,
              tableName: data.tableName,
              description: data.description,
              purpose: data.purpose,
              primaryKeyColumns: data.primaryKeyColumns,
              keywords: data.keywords,
              businessContext: data.businessContext,
            },
          },
        });

        if (responseData?.createTable?.success) {
          if (responseData.createTable.data) {
            dispatch(addTable(responseData.createTable.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedTable());
          setSaveError(null);
          setSuccessMessage('Tabla creada exitosamente');
        } else {
          const errorMessage =
            responseData?.createTable?.message || 'Error al crear tabla';
          const errors = responseData?.createTable?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
        }
      } else {
        if (!data.id) {
          setSaveError('ID de tabla no disponible');
          return;
        }

        const { data: responseData } = await updateTableMutation({
          variables: {
            params: {
              id: data.id,
              schemaId: data.schemaId,
              schemaName: data.schemaName,
              tableName: data.tableName,
              description: data.description,
              purpose: data.purpose,
              primaryKeyColumns: data.primaryKeyColumns,
              keywords: data.keywords,
              businessContext: data.businessContext,
            },
          },
        });

        if (responseData?.updateTable?.success) {
          if (responseData.updateTable.data) {
            dispatch(updateTableAction(responseData.updateTable.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedTable());
          setSaveError(null);
          setSuccessMessage('Tabla actualizada exitosamente');
        } else {
          const errorMessage =
            responseData?.updateTable?.message || 'Error al actualizar tabla';
          const errors = responseData?.updateTable?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
        }
      }
    } catch (err: any) {
      console.error('Error saving table:', err);
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedTable());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedTable());
    setSaveError(null);
    setSaving(false);
  };

  const handleDeleteClick = (table: Table) => {
    setTableToDelete(table);
    setConfirmDeleteOpen(true);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setTableToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!tableToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteTableMutation({
        variables: {
          params: {
            ids: [tableToDelete.id],
          },
        },
      });

      if (responseData?.deleteTable?.success) {
        dispatch(deleteTableAction(tableToDelete.id));
        setConfirmDeleteOpen(false);
        setTableToDelete(null);
        setSuccessMessage('Tabla eliminada exitosamente');
      } else {
        const errorMessage =
          responseData?.deleteTable?.message || 'Error al eliminar tabla';
        const errors = responseData?.deleteTable?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setDeleteError(fullErrorMessage);
      }
    } catch (err: any) {
      console.error('Error deleting table:', err);
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const handleEmbeddingClick = (table: Table) => {
    setTableForEmbedding(table);
    setConfirmEmbeddingOpen(true);
    setEmbeddingError(null);
  };

  const handleCancelEmbedding = () => {
    setConfirmEmbeddingOpen(false);
    setTableForEmbedding(null);
    setEmbeddingError(null);
  };

  const handleConfirmEmbedding = async () => {
    if (!tableForEmbedding) return;

    try {
      setGeneratingEmbedding(true);
      setEmbeddingError(null);

      const { data: responseData } = await createTableEmbeddingMutation({
        variables: {
          params: {
            id: tableForEmbedding.id,
          },
        },
      });

      if (responseData?.createTableEmbedding?.success) {
        setConfirmEmbeddingOpen(false);
        setTableForEmbedding(null);
        setSuccessMessage('Embedding generado exitosamente');
      } else {
        const errorMessage =
          responseData?.createTableEmbedding?.message || 'Error al generar embedding';
        const errors = responseData?.createTableEmbedding?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setEmbeddingError(fullErrorMessage);
      }
    } catch (err: any) {
      console.error('Error generating embedding:', err);
      setEmbeddingError(err.message || 'Error de conexión con el servidor');
    } finally {
      setGeneratingEmbedding(false);
    }
  };

  const columns: GridColDef<Table>[] = [
    {
      field: 'schemaName',
      headerName: 'Esquema',
      width: 120,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/tables/${params.row.id}/configuration`)}
          sx={{ color: '#667eea', fontWeight: 500, cursor: 'pointer' }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'tableName',
      headerName: 'Nombre de la Tabla',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/tables/${params.row.id}/configuration`)}
          sx={{ color: '#667eea', fontWeight: 500, cursor: 'pointer' }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'primaryKeyColumns',
      headerName: 'Columnas PK',
      width: 150,
      valueGetter: (value, row) => row.primaryKeyColumns.join(', '),
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 130,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Generar Embedding">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleEmbeddingClick(params.row)}
            >
              <EmbeddingIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="tables-container">
        <Paper elevation={2} className="tables-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Tablas</Typography>
          </Breadcrumbs>

          <Box className="tables-header">
            <Typography variant="h4" component="h1">
              Tablas de Base de Datos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de tablas del sistema
            </Typography>
          </Box>

          <Box className="tables-toolbar">
            <Box className="filter-box">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Esquema</InputLabel>
                <Select
                  value={schemaFilter}
                  onChange={(e) => handleSchemaFilterChange(e.target.value)}
                  label="Esquema"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {schemas.map((schema) => (
                    <MenuItem key={schema.id} value={schema.id}>
                      {schema.schemaName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Buscar tabla..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 250 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Nueva Tabla
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={tables}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewTableModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditTableModal
          open={createEditModalOpen}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          mode={modalMode}
          saving={saving}
          saveError={saveError}
        />

        <ConfirmDeleteTableDialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          tableName={tableToDelete?.tableName || ''}
          deleting={deleting}
        />

        <ConfirmCreateEmbeddingDialog
          open={confirmEmbeddingOpen}
          onClose={handleCancelEmbedding}
          onConfirm={handleConfirmEmbedding}
          tableName={tableForEmbedding?.tableName || ''}
          generating={generatingEmbedding}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {deleteError && (
          <Snackbar
            open={!!deleteError}
            autoHideDuration={6000}
            onClose={() => setDeleteError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setDeleteError(null)} severity="error" sx={{ width: '100%' }}>
              {deleteError}
            </Alert>
          </Snackbar>
        )}

        {embeddingError && (
          <Snackbar
            open={!!embeddingError}
            autoHideDuration={6000}
            onClose={() => setEmbeddingError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setEmbeddingError(null)} severity="error" sx={{ width: '100%' }}>
              {embeddingError}
            </Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default Tables;
